/**
 * Embed session locale — store, chrome dictionary, and direction handling
 * (spec 075 — Phase 3).
 *
 * The embedded widget can offer multiple languages (agentSettings.embed).
 * The visitor's choice becomes the SESSION LOCALE: it locks the widget
 * chrome (labels + text direction) for the session and is sent with each
 * chat turn so the agent soft-defaults its replies to that language.
 *
 * Scope guarantee: direction (`dir`) is only ever applied when the page runs
 * in embed mode (`embed=true` in the URL) — i.e. inside the widget iframe's
 * own document. The main app never carries that param, so RTL can never leak
 * into the normal UI.
 *
 * Deliberately NOT an i18n framework: a tiny dictionary covering only the
 * widget chrome strings visible to embed visitors.
 */

import { useEffect, useState } from 'react'

/** Locales with full chrome translations. Others fall back to English chrome. */
export type EmbedLocale = string

const STORAGE_KEY = 'taqat_embed_locale'

/** Locales rendered right-to-left. */
const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur'])

/**
 * Whether a locale renders right-to-left.
 *
 * Exported so widget chrome outside this module doesn't need its own copy of the
 * locale list. Two such copies already exist (`form-auto-card`, `show-form-card`);
 * they are left alone here because they are shared with non-embed surfaces, but
 * new callers should use this.
 */
export function isRtlLocale(locale: EmbedLocale | null): boolean {
  return locale !== null && RTL_LOCALES.has(locale)
}

/** Native display names for the language gate / switcher. */
export const LOCALE_LABELS: Record<string, { native: string; english: string; short: string }> = {
  en: { native: 'English', english: 'English', short: 'EN' },
  ar: { native: 'العربية', english: 'Arabic', short: 'ع' },
  fr: { native: 'Français', english: 'French', short: 'FR' },
  es: { native: 'Español', english: 'Spanish', short: 'ES' },
  ur: { native: 'اردو', english: 'Urdu', short: 'اردو' },
  hi: { native: 'हिन्दी', english: 'Hindi', short: 'हि' },
}

/** Widget chrome strings per locale. English is the universal fallback. */
const CHROME_STRINGS = {
  en: {
    chooseLanguage: 'Choose your language',
    chooseLanguageSub: 'Select a language to continue',
    welcomeTo: (name: string) => `Welcome to ${name}`,
    askAnythingStarters: 'Ask anything or try one of these suggestions',
    askAnything: 'Ask anything to get started',
    composerPlaceholder: 'Type a message…',
    disclaimer:
      'Chat output is generated by AI and may not always be accurate. Please verify the information before taking any action.',
    poweredBy: 'Powered by Taqat',
    closeChat: 'Close chat',
    switchLanguage: 'Switch language',
    workingOnRequest: 'Working on your request…',
    stoppingResponse: 'Stopping response…',
    suggested: 'Suggested',
    closeForm: 'Close form',
    formSubtitle: 'Fill in the details to continue',
    formOpened: 'Form opened',
    formClosed: 'Form closed — your answers are kept',
    reopenForm: 'Reopen form',
  },
  ar: {
    chooseLanguage: 'اختر لغتك',
    chooseLanguageSub: 'اختر لغة للمتابعة',
    welcomeTo: (name: string) => `أهلاً بك في ${name}`,
    askAnythingStarters: 'اسأل عن أي شيء أو اختر أحد الاقتراحات',
    askAnything: 'اسأل عن أي شيء للبدء',
    composerPlaceholder: 'اكتب رسالة…',
    disclaimer:
      'قد تكون مخرجات المحادثة غير دقيقة دائمًا، يرجى التحقق من المعلومات قبل اتخاذ أي إجراء.',
    poweredBy: 'مشغَّل بواسطة تقات',
    closeChat: 'إغلاق المحادثة',
    switchLanguage: 'تغيير اللغة',
    workingOnRequest: 'جارٍ العمل على طلبك…',
    stoppingResponse: 'جارٍ إيقاف الرد…',
    suggested: 'اقتراحات',
    closeForm: 'إغلاق النموذج',
    formSubtitle: 'أكمل البيانات للمتابعة',
    formOpened: 'تم فتح النموذج',
    formClosed: 'تم إغلاق النموذج — إجاباتك محفوظة',
    reopenForm: 'إعادة فتح النموذج',
  },
} as const

export type ChromeStrings = (typeof CHROME_STRINGS)['en']

/** Chrome strings for a locale (English fallback for untranslated locales). */
export function getChromeStrings(locale: EmbedLocale | null): ChromeStrings {
  if (locale && locale in CHROME_STRINGS) {
    return CHROME_STRINGS[locale as keyof typeof CHROME_STRINGS]
  }
  return CHROME_STRINGS.en
}

/** True when the page runs inside the embedded widget iframe. */
export function isEmbedSurface(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('embed') === 'true'
  } catch {
    return false
  }
}

// ── Store (module-level, use-tool-visibility pattern) ──────────────────────

let currentLocale: EmbedLocale | null = null
let initialized = false
const listeners = new Set<() => void>()

function readInitialLocale(): EmbedLocale | null {
  // 1. `?locale=` on the iframe URL (host page preset) wins
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('locale')
    if (fromUrl && /^[a-z]{2}(-[a-z]{2})?$/.test(fromUrl)) return fromUrl
  } catch { /* ignore */ }
  // 2. Session persistence (survives reloads and iframe re-creation in-tab)
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY)
    if (stored && /^[a-z]{2}(-[a-z]{2})?$/.test(stored)) return stored
  } catch { /* ignore */ }
  return null
}

function ensureInit(): void {
  if (initialized) return
  initialized = true
  currentLocale = readInitialLocale()
  applyDocumentDirection(currentLocale)
}

/** The active embed session locale, or null when none chosen. */
export function getEmbedLocale(): EmbedLocale | null {
  ensureInit()
  return currentLocale
}

/**
 * Sets the embed session locale: persists it, flips the iframe document
 * direction (embed surface only), and notifies subscribers.
 */
export function setEmbedLocale(locale: EmbedLocale): void {
  ensureInit()
  currentLocale = locale
  try {
    window.sessionStorage.setItem(STORAGE_KEY, locale)
  } catch { /* private mode — in-memory only */ }
  applyDocumentDirection(locale)
  for (const l of listeners) l()
}

/**
 * Applies `dir` + `lang` to the document root — ONLY on the embed surface
 * (the widget iframe's own document; the main app is never affected).
 */
function applyDocumentDirection(locale: EmbedLocale | null): void {
  if (!isEmbedSurface()) return
  const root = document.documentElement
  if (!locale) return
  root.setAttribute('lang', locale)
  root.setAttribute('dir', RTL_LOCALES.has(locale) ? 'rtl' : 'ltr')
}

/** React hook: subscribes to the embed session locale. */
export function useEmbedLocale(): EmbedLocale | null {
  ensureInit()
  const [, force] = useState(0)
  useEffect(() => {
    const listener = () => force((n) => n + 1)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])
  return currentLocale
}

// ── Localized-content resolution ───────────────────────────────────────────

/**
 * Resolves a per-locale map (e.g. `{ en: [...], ar: [...] }`) for the active
 * locale with sensible fallbacks: exact locale → English → first available.
 * Returns undefined when the map is empty/absent so callers can fall back to
 * legacy flat fields.
 */
export function resolveLocalized<T>(
  map: Record<string, T> | null | undefined,
  locale: EmbedLocale | null
): T | undefined {
  if (!map) return undefined
  if (locale && map[locale] !== undefined) return map[locale]
  if (map.en !== undefined) return map.en
  const first = Object.values(map)[0]
  return first
}
